<template>
  <transition name="modal-fade">
    <div
      v-if="visible"
      class="register-modal-overlay"
      @click.self="handleClose"
    >
      <div class="register-modal-container">
        <div class="register-header">
          <h2 class="register-title">{{ $t("register_modal.title") }}</h2>
          <button
            class="modal-close-btn"
            @click="handleClose"
            :aria-label="$t('common.close')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form class="register-form" @submit.prevent="handleRegister">
          <!-- 找回方式切换 -->
          <div class="recovery-tabs">
            <button
              type="button"
              class="recovery-tab"
              :class="{ active: recoveryMethod === 'email' }"
              @click="switchMethod('email')"
            >
              {{ $t("forgot_password_modal.method_email_placeholder") }}
            </button>
            <button
              type="button"
              class="recovery-tab"
              :class="{ active: recoveryMethod === 'phone' }"
              @click="switchMethod('phone')"
            >
              {{ $t("forgot_password_modal.method_phone_placeholder") }}
            </button>
          </div>
          <div class="name-row">
            <select
              v-model="form.salutation"
              class="form-select salutation-select"
            >
              <option value="mr">
                {{ $t("register_modal.salutation.mr") }}
              </option>
              <option value="mrs">
                {{ $t("register_modal.salutation.mrs") }}
              </option>
              <option value="ms">
                {{ $t("register_modal.salutation.ms") }}
              </option>
            </select>
            <input
              v-model="form.name"
              type="text"
              class="form-input"
              :placeholder="$t('register_modal.name')"
            />
          </div>

          <input
            v-model="form.email"
            type="email"
            class="form-input"
            :placeholder="$t('register_modal.email')"
          />
          <!-- 验证码 -->
          <div v-if="recoveryMethod == 'email'" class="captcha-row">
            <input
              v-model="form.captcha"
              type="text"
              class="form-input captcha-input"
              :placeholder="$t('register_modal.captcha_placeholder')"
              maxlength="6"
              autocomplete="off"
            />
            <button
              type="button"
              class="btn-captcha"
              :disabled="captchaCooldown > 0"
              @click="sendCaptcha"
            >
              {{
                captchaCooldown > 0
                  ? $t("register_modal.resend_countdown", {
                      seconds: captchaCooldown,
                    })
                  : $t("register_modal.send_captcha")
              }}
            </button>
          </div>
          <input
            v-model="form.address"
            type="text"
            class="form-input"
            :placeholder="$t('register_modal.address')"
          />
          <!-- 微信号 -->
          <input
            v-model="form.weixin"
            type="text"
            class="form-input"
            :placeholder="$t('register_modal.wechat')"
          />
          <div class="phone-row">
            <select
              v-model="form.phoneRegion"
              class="form-select phone-region-select"
            >
              <option value="+86">
                {{ $t("account_modal.address_form.phone_regions.cn") }}
              </option>
            </select>

            <input
              v-model="form.phone"
              type="tel"
              autocomplete="off"
              class="form-input phone-input"
              :placeholder="$t('register_modal.phone')"
            />
          </div>

          <!-- 验证码 -->
          <div v-if="recoveryMethod == 'phone'" class="captcha-row">
            <input
              v-model="form.captcha"
              type="text"
              class="form-input captcha-input"
              :placeholder="$t('register_modal.captcha_placeholder')"
              maxlength="6"
              autocomplete="off"
            />
            <button
              type="button"
              class="btn-captcha"
              :disabled="captchaCooldown > 0"
              @click="sendCaptcha"
            >
              {{
                captchaCooldown > 0
                  ? $t("register_modal.resend_countdown", {
                      seconds: captchaCooldown,
                    })
                  : $t("register_modal.send_captcha")
              }}
            </button>
          </div>

          <div class="password-row">
            <input
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="off"
              class="form-input password-input"
              :placeholder="$t('register_modal.password')"
            />
            <button
              type="button"
              class="btn-toggle-password"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <svg
                v-if="!showPassword"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            </button>
          </div>

          <div class="password-row">
            <input
              v-model="form.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              autocomplete="off"
              class="form-input password-input"
              :placeholder="$t('register_modal.confirm_password')"
            />
            <button
              type="button"
              class="btn-toggle-password"
              @click="showConfirmPassword = !showConfirmPassword"
              :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
            >
              <svg
                v-if="!showConfirmPassword"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            </button>
          </div>

          <div class="birthday-row">
            <span class="field-label">{{
              $t("register_modal.birth_date")
            }}</span>
            <div class="date-selects">
              <select v-model="form.birthYear" class="form-select date-select">
                <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
              </select>
              <select v-model="form.birthMonth" class="form-select date-select">
                <option v-for="m in 12" :key="m" :value="m">{{ m }}</option>
              </select>
              <select v-model="form.birthDay" class="form-select date-select">
                <option v-for="d in 31" :key="d" :value="d">{{ d }}</option>
              </select>
            </div>
          </div>

          <div class="language-row">
            <span class="field-label">{{ $t("register_modal.language") }}</span>
            <select v-model="form.language" class="form-select lang-select">
              <option value="繁體中文">
                {{ $t("register_modal.lang_zh_tw") }}
              </option>
              <option value="中文简体">
                {{ $t("register_modal.lang_zh_cn") }}
              </option>
              <option value="English">
                {{ $t("register_modal.lang_en") }}
              </option>
            </select>
          </div>

          <label class="agreement-row">
            <input v-model="form.agreed" type="checkbox" />
            <span>{{ $t("register_modal.agreement") }}</span>
          </label>

          <p class="agreement-note">
            {{ $t("register_modal.agreement_note") }}
          </p>

          <button type="submit" class="btn-register">
            {{ $t("register_modal.register") }}
          </button>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions } from "vuex";
import { userApi } from "@/api/user";

export default {
  name: "RegisterModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      recoveryMethod: "email",
      years: Array.from({ length: 80 }, (_, i) => currentYear - 18 - i),
      captchaCooldown: 0,
      captchaTimer: null,
      smsCode: "",
      showPassword: false,
      showConfirmPassword: false,
      form: {
        salutation: "mr",
        name: "",
        email: "",
        address: "",
        phoneRegion: "+86",
        phone: "",
        captcha: "",
        password: "",
        confirmPassword: "",
        birthYear: 2005,
        birthMonth: 1,
        birthDay: 1,
        language: "繁體中文",
        agreed: false,
        weixin:""
      },
    };
  },
  watch: {
    visible(val) {
      if (val) {
        document.body.style.overflow = "hidden";
      } else {
        this.resetBodyOverflow();
      }
    },
  },
  beforeDestroy() {
    if (this.captchaTimer) {
      clearInterval(this.captchaTimer);
    }
  },
  methods: {
    ...mapActions(["register", "closeRegisterModal", "showToast"]),
    handleClose() {
      this.closeRegisterModal();
      this.$emit("close");
    },
    switchMethod(method) {
      this.recoveryMethod = method;
    },
    async sendCaptcha() {
      if (this.recoveryMethod == "email") {
        //邮箱
        if (!this.form.email) {
          this.showToast({
            message: this.$t("register_modal.validation.email_required"),
            type: "warning",
          });
          return;
        }

        try {
          const res = await userApi.sendEmail({ email: this.form.email });
          if (res.code === 1) {
            this.smsCode = res.data || "";
            this.showToast({
              message: this.$t("register_modal.validation.captcha_sent"),
              type: "success",
            });
          } else {
            this.showToast({
              message: res.msg || "验证码发送失败",
              type: "error",
            });
            return;
          }
        } catch {
          this.showToast({
            message: "验证码发送失败，请稍后重试",
            type: "error",
          });
          return;
        }

        // 开始倒计时
        this.captchaCooldown = 60;
        this.captchaTimer = setInterval(() => {
          this.captchaCooldown--;
          if (this.captchaCooldown <= 0) {
            clearInterval(this.captchaTimer);
            this.captchaTimer = null;
          }
        }, 1000);
      } else {
        //手机号
        if (!this.form.phone) {
          this.showToast({
            message: this.$t("register_modal.validation.phone_required"),
            type: "warning",
          });
          return;
        }

        try {
          const res = await userApi.sendCode({ phone: this.form.phone });
          if (res.code === 1) {
            this.smsCode = res.data || "";
            this.showToast({
              message: this.$t("register_modal.validation.captcha_sent"),
              type: "success",
            });
          } else {
            this.showToast({
              message: res.msg || "验证码发送失败",
              type: "error",
            });
            return;
          }
        } catch {
          this.showToast({
            message: "验证码发送失败，请稍后重试",
            type: "error",
          });
          return;
        }

        // 开始倒计时
        this.captchaCooldown = 60;
        this.captchaTimer = setInterval(() => {
          this.captchaCooldown--;
          if (this.captchaCooldown <= 0) {
            clearInterval(this.captchaTimer);
            this.captchaTimer = null;
          }
        }, 1000);
      }
      //邮箱
    },
    async handleRegister() {
      const { email, password, confirmPassword, name, phone, captcha, agreed, address, weixin } = this.form;
      if (!name || !email || !phone || !captcha || !password || !address || !weixin) {
        this.showToast({
          message: this.$t("register_modal.validation.all_required"),
          type: "warning",
        });
        return;
      }
      if (password !== confirmPassword) {
        this.showToast({
          message: this.$t("register_modal.validation.password_mismatch"),
          type: "warning",
        });
        return;
      }
      if (!agreed) {
        this.showToast({
          message: this.$t("register_modal.validation.agree_terms"),
          type: "warning",
        });
        return;
      }

      // 验证方式：1=邮箱 2=手机号
      const type = this.recoveryMethod === "email" ? 1 : 2;

      // 传递原始表单数据给store，由store负责映射
      const registerData = {
        ...this.form,
        type,
      };

      const ok = await this.register(registerData);
      if (ok) {
        this.resetForm();
      }
    },
    resetForm() {
      this.smsCode = "";
      this.form = {
        salutation: "mr",
        name: "",
        email: "",
        address: "",
        phoneRegion: "+86",
        phone: "",
        captcha: "",
        password: "",
        confirmPassword: "",
        birthYear: 2005,
        birthMonth: 1,
        birthDay: 1,
        language: "繁體中文",
        agreed: false,
        weixin:""
      };
    },
    resetBodyOverflow() {
      const {
        cartModalVisible,
        loginModalVisible,
        forgotPasswordModalVisible,
      } = this.$store.state;
      if (
        !cartModalVisible &&
        !loginModalVisible &&
        !forgotPasswordModalVisible
      ) {
        document.body.style.overflow = "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.register-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
}

.register-modal-container {
  background: #fff;
  width: 480px;
  max-width: 94vw;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px 36px 32px;
  border-radius: 4px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
}

.register-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.register-title {
  font-size: 20px;
  color: #333;
  font-weight: 500;
  margin: 0;
}

.modal-close-btn {
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  color: #bbb;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;

  &:hover {
    color: #666;
  }
}

.register-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.recovery-tabs {
  display: flex;
  gap: 0;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.recovery-tab {
  flex: 1;
  padding: 12px 16px;
  background: #fff;
  border: none;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;

  &:not(:last-child) {
    border-right: 1px solid #e0e0e0;
  }

  &.active {
    background: #8b7e55;
    color: #fff;
  }

  &:hover:not(.active) {
    background: #f5f5f5;
  }
}
.name-row {
  display: flex;
  gap: 10px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  background: #fff;
  font-size: 14px;
  color: #333;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.3s;

  &::placeholder {
    color: #bbb;
  }

  &:focus {
    border-color: #8b7e55;
  }
}

.salutation-select {
  width: 90px;
  flex-shrink: 0;
}

.name-row .form-input {
  flex: 1;
}

.captcha-row {
  display: flex;
  gap: 10px;
}

.captcha-input {
  flex: 1;
}

.btn-captcha {
  width: 120px;
  padding: 12px 16px;
  background: #8b7e55;
  color: #fff;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.3s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #7a6d4a;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
}

.birthday-row,
.language-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.field-label {
  font-size: 14px;
  color: #666;
  white-space: nowrap;
  min-width: 72px;
}

.date-selects {
  display: flex;
  gap: 8px;
  flex: 1;
}

.date-select {
  flex: 1;
}

.lang-select {
  flex: 1;
}

.agreement-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 12px;
  color: #666;
  line-height: 1.6;
  cursor: pointer;
  margin-top: 8px;

  input {
    margin-top: 3px;
    flex-shrink: 0;
    accent-color: #8b7e55;
  }
}

.agreement-note {
  font-size: 11px;
  color: #aaa;
  line-height: 1.6;
  margin: 0;
}

.btn-register {
  width: 100%;
  padding: 14px;
  background: #3a3a3a;
  color: #fff;
  border: none;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 12px;
  transition: background 0.3s;
  letter-spacing: 1px;

  &:hover {
    background: #2a2a2a;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active .register-modal-container,
.modal-fade-leave-active .register-modal-container {
  transition: transform 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .register-modal-container,
.modal-fade-leave-to .register-modal-container {
  transform: scale(0.96);
}
.phone-row {
  display: flex;
  gap: 10px;
}

.phone-region-select {
  width: 140px;
  flex-shrink: 0;
}

.phone-input {
  flex: 1;
}

.password-row {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input {
  flex: 1;
  padding-right: 48px;
}

.btn-toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
  padding: 0;

  &:hover {
    color: #666;
  }

  &:focus {
    outline: none;
  }

  svg {
    display: block;
  }
}

@media (max-width: 480px) {
  .register-modal-container {
    padding: 20px 20px 24px;
  }

  .birthday-row,
  .language-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .date-selects {
    width: 100%;
  }

  .lang-select {
    width: 100%;
  }

  .captcha-row {
    flex-direction: column;
  }

  .btn-captcha {
    width: 100%;
  }
}
</style>
